import React from 'react';
import { Link } from 'react-router-dom';
import {
  Folder, Tag, Archive, BarChart3, Zap, CreditCard, Megaphone,
  LogOut, User, Moon, Sun, Download, HelpCircle, Users, BookOpen, LifeBuoy
} from 'lucide-react';
import { CATEGORIES } from '@/constants/dashboard';

const Sidebar = ({
  user, dark, toggleTheme, sidebarOpen, setSidebarOpen,
  selectedCategory, setSelectedCategory, selectedTags, toggleTagFilter, allTags,
  showArchived, setShowArchived, setShowStats, setShowDownloadGuide,
  setShowHelpTutorial, logout, onExportIdeas
}) => {
  return (
    <aside className={`w-72 bg-[var(--surface)] border-r border-[var(--border-default)] h-screen p-6 flex flex-col overflow-y-auto transition-transform duration-300 z-40 
      fixed top-0 left-0 lg:static lg:translate-x-0 ${
      sidebarOpen ? 'translate-x-0' : '-translate-x-full'
    }`}>
      <div className="mb-8">
        <h1 className="font-black text-2xl lg:text-3xl tracking-tighter leading-none" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          VAULT
        </h1>
        <p className="text-xs font-mono uppercase tracking-wider text-[var(--text-secondary)] mt-1">Idea Storage</p>
        {user && (
          <div className="mt-4 pt-4 border-t border-[var(--border-subtle)]">
            <div className="flex items-center gap-2">
              <User size={16} className="text-[var(--text-secondary)]" />
              <span className="text-xs text-[var(--text-secondary)]">{user.name || user.email}</span>
            </div>
          </div>
        )}
      </div>

      {/* Categories */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Folder size={16} />
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Categories</span>
        </div>
        <div className="space-y-1">
          <button
            data-testid="category-all-btn"
            onClick={() => { setSelectedCategory('All'); setSidebarOpen(false); }}
            className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors duration-150 ${
              selectedCategory === 'All'
                ? 'selected-state'
                : 'hover:bg-[var(--surface-alt)]'
            }`}
          >
            All Ideas
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              data-testid={`category-${cat.toLowerCase()}-btn`}
              onClick={() => { setSelectedCategory(cat); setSidebarOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                selectedCategory === cat
                  ? 'selected-state'
                  : 'hover:bg-[var(--surface-alt)]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Tags Filter */}
      {allTags.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Tag size={16} />
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Tags</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {allTags.map(tag => (
              <button
                key={tag}
                data-testid={`tag-filter-${tag}`}
                onClick={() => toggleTagFilter(tag)}
                className={`inline-flex items-center px-2 py-1 border border-[var(--border-default)] text-xs font-mono uppercase tracking-wider transition-colors duration-150 ${
                  selectedTags.includes(tag)
                    ? 'selected-state'
                    : 'bg-[var(--surface)] hover:bg-[var(--surface-alt)]'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom Actions */}
      <div className="mt-auto pt-6 border-t border-[var(--border-subtle)] space-y-2">
        {(!user?.subscription_tier || user?.subscription_tier === 'free') && (
          <Link
            to="/pricing"
            data-testid="upgrade-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium bg-[#007AFF] text-white hover:opacity-90 transition-opacity border border-[var(--border-default)] mb-2"
          >
            <Zap size={16} />
            Upgrade to Pro
          </Link>
        )}
        {user?.subscription_tier && user?.subscription_tier !== 'free' && (
          <Link
            to="/subscription"
            data-testid="subscription-manage-btn"
            className="px-3 py-2 bg-[#FFD60A]/20 border border-[#FFD60A] mb-2 flex items-center gap-2 hover:bg-[#FFD60A]/30 transition-colors"
          >
            <CreditCard size={14} />
            <span className="text-xs font-mono uppercase tracking-wider text-[var(--obsidian)] font-bold">
              {user.subscription_tier} Plan
            </span>
          </Link>
        )}
        <Link
          to="/pricing"
          data-testid="pricing-btn"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--surface-alt)] transition-colors duration-150"
        >
          <CreditCard size={16} />
          Pricing
        </Link>
        {user?.role === 'admin' && (
          <Link
            to="/marketing"
            data-testid="marketing-hub-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--secondary)] hover:text-white transition-colors duration-150 border border-[var(--border-default)] bg-[var(--secondary)]/10 mb-2"
          >
            <Megaphone size={16} />
            Marketing Hub
          </Link>
        )}
        {user?.role === 'admin' && (
          <Link
            to="/admin"
            data-testid="admin-dashboard-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--primary)] hover:text-white transition-colors duration-150 border border-[var(--border-default)] bg-[var(--primary)]/10"
          >
            <BarChart3 size={16} />
            Admin Dashboard
          </Link>
        )}
        <button
          data-testid="view-stats-btn"
          onClick={() => { setShowStats(true); setSidebarOpen(false); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--surface-alt)] transition-colors duration-150"
        >
          <BarChart3 size={16} />
          View Stats
        </button>
        <button
          data-testid="toggle-archive-btn"
          onClick={() => { setShowArchived(!showArchived); setSidebarOpen(false); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--surface-alt)] transition-colors duration-150"
        >
          <Archive size={16} />
          {showArchived ? 'Show Active' : 'Show Archived'}
        </button>
        {user?.role === 'admin' && (
          <Link
            to="/blog-admin"
            data-testid="blog-admin-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--surface-alt)] transition-colors duration-150"
          >
            <BookOpen size={16} />
            Blog Manager
          </Link>
        )}
        <Link
          to="/support"
          data-testid="support-btn"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--surface-alt)] transition-colors duration-150"
        >
          <LifeBuoy size={16} />
          Support & FAQ
        </Link>
        <Link
          to="/profile"
          data-testid="profile-btn"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--surface-alt)] transition-colors duration-150"
        >
          <User size={16} />
          My Profile
        </Link>
        <button
          data-testid="export-ideas-btn"
          onClick={onExportIdeas}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--surface-alt)] transition-colors duration-150"
        >
          <Download size={16} />
          Export Ideas
        </button>
        <button
          data-testid="toggle-theme-btn"
          onClick={toggleTheme}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--surface-alt)] transition-colors duration-150"
        >
          {dark ? <Sun size={16} /> : <Moon size={16} />}
          {dark ? 'Light Mode' : 'Dark Mode'}
        </button>
        <button
          data-testid="download-guide-btn"
          onClick={() => { setShowDownloadGuide(true); setSidebarOpen(false); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--surface-alt)] transition-colors duration-150"
        >
          <Download size={16} />
          How to Download
        </button>
        <button
          data-testid="help-tutorial-btn"
          onClick={() => { setShowHelpTutorial(true); setSidebarOpen(false); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--surface-alt)] transition-colors duration-150"
        >
          <HelpCircle size={16} />
          Help & Tutorials
        </button>
        {(user?.subscription_tier === 'business' || user?.subscription_tier === 'founding' || user?.role === 'admin') && (
          <Link
            to="/teams"
            data-testid="teams-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--surface-alt)] transition-colors duration-150 border border-[var(--border-subtle)]"
          >
            <Users size={16} />
            Teams
          </Link>
        )}
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-[var(--primary)] hover:text-white transition-colors duration-150"
        >
          <LogOut size={16} />
          Sign Out
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
